import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { ButtercreamCalculatorClient } from './ui/ButtercreamCalculatorClient';

interface ButtercreamCalculatorProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const ButtercreamCalculator: React.FC<ButtercreamCalculatorProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Free Buttercream Quantity Calculator"
            description="Estimate the exact amount of buttercream frosting you need for any size cake with our free calculator, including layers, crumb coat, and final decorations."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="buttercream-calculator"
        >
            <ButtercreamCalculatorClient />
            <ButtercreamCalculatorContent />
        </ToolContainer>
    );
};

const ButtercreamCalculatorContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border">
        {/* SEO Intro Section */}
        <div className="prose prose-pink max-w-none text-brand-text-body">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">The Ultimate Buttercream Frosting Calculator</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                One of the most common questions bakers ask is: <strong>"How much buttercream do I need for a cake?"</strong> Running out of frosting halfway through decorating is a nightmare, but making way too much is a waste of money. Our <strong>Frosting Calculator</strong> solves this problem. Whether you are using <strong>American Buttercream</strong>, <strong>Swiss Meringue</strong>, or <strong>Ganache</strong>, this tool calculates the exact volume required for filling, crumb coating, and decorating your cake tiers.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Calculate Frosting Quantity
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Define Your Tiers:</strong> select the shape (Round/Square) and size for each tier. Add multiple tiers for <strong>Wedding Cakes</strong>.</li>
                    <li><strong>Choose Your Style:</strong> Standard, Naked, Generous, or Extra.</li>
                    <li><strong>View Results:</strong> get your total in Cups, Grams, and Pounds.</li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm text-brand-text-body">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            Does this work for Swiss Meringue Buttercream (SMBC)?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Yes! This tool calculates <strong>Volume (Cups)</strong> which applies to all frosting types.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
